import React, {forwardRef, memo, useMemo} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";

const calculateWidth = ({cols, rightGap, isAdaptive, fixedWidth, autoWidth}) => {
  if (isAdaptive && !autoWidth) {
    return "100%";
  } else if (fixedWidth) {
    return fixedWidth + "px";
  } else if (autoWidth) {
    return "auto";
  } else {
    return `calc(${cols * (100 / 12)}% - ${rightGap}px)`;
  }
  // isAdaptive ? "100%" : `calc(${cols * (100 / 12)}% - ${rightGap}px)`
};

const BlockContainer = memo(
  forwardRef(
    (
      {
        flow,
        cols,
        children,
        autoWidth,
        rightGap,
        bottomGap,
        isAdaptive,
        needColsAttribute,
        needAlignItems,
        className,
        isTurntable,
        onlyPreview,
        fixedWidth,
        isTurntableContainer,
        justifyContent,
        alignItems,
        style,
      },
      ref
    ) => {
      const dataAttrs = useMemo(() => {
        const dataAttrs = {};
        if (needColsAttribute) dataAttrs["data-cols"] = cols;
        if (isTurntable) dataAttrs["data-turntable-container"] = true;
        if (isTurntableContainer) dataAttrs["data-turntable-widget"] = true;
        return dataAttrs;
      }, [cols, isTurntable, isTurntableContainer, needColsAttribute]);

      let right = rightGap;
      if (isTurntable) {
        right = 0;
      }

      return (
        <Wrapper
          {...dataAttrs}
          fixedWidth={fixedWidth}
          flow={flow}
          cols={cols}
          autoWidth={autoWidth}
          rightGap={right}
          bottomGap={bottomGap}
          needAlignItems={needAlignItems}
          isAdaptive={isAdaptive}
          className={className}
          isTurntable={isTurntable}
          onlyPreview={onlyPreview}
          propsRef={ref}
          justifyContent={justifyContent}
          alignItems={alignItems}
          style={style}
        >
          {children}
        </Wrapper>
      );
    }
  )
);

BlockContainer.displayName = "BlockContainer";

const Wrapper = styled(
  ({
    cols,
    flow,
    rightGap,
    bottomGap,
    autoWidth,
    isAdaptive,
    needAlignItems,
    isTurntable,
    onlyPreview,
    fixedWidth,
    propsRef,
    justifyContent,
    alignItems,
    style,
    ...props
  }) => {
    return <div {...props} ref={propsRef} />;
  }
)`
  display: flex;
  flex-wrap: wrap;
  flex-direction: ${({flow}) => (flow === "vertical" ? "column" : "row")};
  width: ${({cols, rightGap, isAdaptive, fixedWidth, autoWidth}) =>
    calculateWidth({cols, rightGap, isAdaptive, fixedWidth, autoWidth})};
  flex: ${({autoWidth}) => autoWidth && "1"};
  margin-right: ${({rightGap}) => rightGap}px;
  min-height: 0;
  min-width: 0;
    //height: ${(height) => height};
  align-items: ${(needAlignItems) => (needAlignItems ? "stretch" : "flex-start")};
  margin-bottom: ${({bottomGap}) => bottomGap}px;

    ${({justifyContent}) =>
      justifyContent &&
      css`
        justify-content: ${justifyContent};
      `}

    ${({alignItems}) =>
      alignItems &&
      css`
        align-items: ${alignItems};
      `}

  :last-child {
    margin-right: 0;
    margin-bottom: 0;
  }

  ${({isTurntable}) =>
    isTurntable &&
    css`
      flex-grow: 1;
      width: calc(
        ${({cols, onlyPreview, isAdaptive}) =>
            onlyPreview || isAdaptive ? "100%" : cols * (100 / 12)}
          %
      );
    `}
}

  @media screen and (max-width: ${({theme}) => theme.breakpoints.values.md}px) {
    width: ${({rightGap}) => `calc(100% - ${rightGap}px)`};
  }

    ${({style}) => {
      if (style) {
        return style;
      }
    }}
`;

BlockContainer.propTypes = {
  children: PropTypes.any.isRequired,
  flow: PropTypes.string,
  cols: PropTypes.number,
  autoWidth: PropTypes.bool,
  rightGap: PropTypes.number,
  isAdaptive: PropTypes.bool,
  bottomGap: PropTypes.number,
  needAlignItems: PropTypes.bool,
  className: PropTypes.string,
  isTurntable: PropTypes.bool,
  isTurntableContainer: PropTypes.bool,
};

BlockContainer.defaultProps = {
  rightGap: 0,
  isAdaptive: false,
  bottomGap: 0,
  needAlignItems: false,
  className: "",
  cols: 12,
  isTurntable: false,
  isTurntableContainer: false,
};

export default BlockContainer;
